
import { Check } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";

export type Step = {
  id: string;
  title: string;
  icon: React.ReactNode;
};

interface StepIndicatorProps {
  steps: Step[];
  currentStep: number;
  onStepClick?: (stepIndex: number) => void;
}

export const StepIndicator = ({ steps, currentStep, onStepClick }: StepIndicatorProps) => {
  const { t } = useLanguage();

  // Calculate progress percentage - fixed calculation for consistent progression
  const totalSegments = steps.length - 1;
  const progressPercentage = totalSegments <= 0 
    ? 0 
    : Math.min(100, Math.round((currentStep / totalSegments) * 100));

  return (
    <div className="relative flex w-full justify-between mb-10 sm:mb-14 px-1 sm:px-4">
      {/* Background line connecting all steps */}
      <div className="absolute top-[18px] sm:top-6 left-0 w-full h-1 bg-muted dark:bg-muted rounded-full -z-0"></div>
      
      {/* Progress line - using fixed percentage calculation with adjustment for step connection */}
      <div 
        className="absolute top-[18px] sm:top-6 left-0 h-1 bg-primary rounded-full transition-all duration-500 ease-in-out z-0" 
        style={{ 
          width: `calc(${progressPercentage}% + ${currentStep > 0 ? '16px' : '0px'})`,
          left: '-8px' 
        }}
      ></div>
      
      {steps.map((step, index) => {
        const isCompleted = index < currentStep;
        const isCurrent = index === currentStep;
        const isClickable = index < currentStep && onStepClick;
        
        return (
          <div
            key={step.id}
            className={cn(
              "flex flex-col items-center relative z-10",
              isClickable && "cursor-pointer group"
            )}
            onClick={() => isClickable && onStepClick?.(index)}
          >
            {/* Step circle */}
            <div
              className={cn(
                "flex h-9 w-9 sm:h-12 sm:w-12 items-center justify-center rounded-full border-2 transition-all duration-300",
                isCompleted
                  ? "bg-primary border-none text-white shadow-md"
                  : isCurrent
                  ? "border-primary text-primary scale-110 bg-card dark:bg-card shadow-lg"
                  : "border-border bg-card dark:bg-card text-muted-foreground"
              )}
            >
              {isCompleted ? (
                <Check className="h-4 w-4 sm:h-5 sm:w-5" />
              ) : (
                <span className="text-xs sm:text-sm font-medium">{index + 1}</span>
              )}
            </div>

            {/* Step title */}
            <div className="flex flex-col items-center mt-2 sm:mt-3">
              <span
                className={cn(
                  "text-[10px] sm:text-xs font-medium transition-colors duration-300 text-center leading-tight",
                  isCompleted 
                    ? "text-primary" 
                    : isCurrent 
                    ? "text-primary font-semibold" 
                    : "text-muted-foreground"
                )}
              >
                {t(step.title)}
              </span>
              
              {/* Tooltip for clickable steps */}
              {isClickable && (
                <span className="absolute -bottom-8 whitespace-nowrap text-xs bg-primary text-white px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  {t('clickToReturn')}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const defaultSteps: Step[] = [
  {
    id: "location",
    title: "locations",
    icon: null,
  },
  {
    id: "time",
    title: "time",
    icon: null,
  },
  {
    id: "content",
    title: "content",
    icon: null,
  },
  {
    id: "information",
    title: "information",
    icon: null,
  },
  {
    id: "payment",
    title: "payment",
    icon: null,
  },
];
